// File: src/pages/PurchaseReport.jsx

import React, { useEffect, useState, useRef } from 'react';
import dayjs           from 'dayjs';
import jsPDF           from 'jspdf';
import autoTable       from 'jspdf-autotable';
import * as XLSX       from 'xlsx';
import api             from '../api';
import { toGSTR2JSON } from '../utils/gstr2';

export default function PurchaseReport() {
  // Date range state
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to,   setTo]   = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

  // Data & UI state
  const [bills, setBills]             = useState([]);
  const [settings, setSettings]       = useState({});
  const [suppliers, setSuppliers]     = useState([]);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail]             = useState('');
  const [sending, setSending]         = useState(false);
  const printRef                      = useRef();

  // Fetch settings & suppliers once
  useEffect(() => {
    api.get('/settings/get').then(res => setSettings(res.data || {}));
    api.get('/suppliers'    ).then(res => setSuppliers(res.data || []));
  }, []);

  // Fetch bills whenever range changes
  useEffect(() => {
    fetchData();
  }, [from, to]);

  const fetchData = async () => {
    try {
      const res = await api.get('/purchase-reports', { params: { from, to } });
      setBills(res.data.bills || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Reset to current month
  const handleThisMonth = () => {
    setFrom(dayjs().startOf('month').format('YYYY-MM-DD'));
    setTo(  dayjs().endOf('month').format('YYYY-MM-DD'));
  };

  // Filtered list
  const filteredBills = bills.filter(b =>
    (!filterSupplier || String(b.SupplierID) === filterSupplier) &&
    (!filterStatus   || b.Status === filterStatus)
  );

  // Per-bill sums
  const calculateSums = bill => {
    const items    = bill.items || [];
    const taxable  = items.reduce((s,i)=>s + Number(i.Amount || 0), 0);
    const cgst     = items.reduce((s,i)=>s + Number(i.CGST   || 0), 0);
    const sgst     = items.reduce((s,i)=>s + Number(i.SGST   || 0), 0);
    const igst     = items.reduce((s,i)=>s + Number(i.IGST   || 0), 0);
    const totalGST = cgst + sgst + igst;
    const total    = taxable + totalGST - Number(bill.Discount || 0);
    return { taxable, cgst, sgst, igst, totalGST, total };
  };

  // Grand totals
  const grandTotals = filteredBills.reduce((acc,b) => {
    const s = calculateSums(b);
    acc.taxable  += s.taxable;
    acc.cgst     += s.cgst;
    acc.sgst     += s.sgst;
    acc.igst     += s.igst;
    acc.totalGST += s.totalGST;
    acc.total    += s.total;
    return acc;
  }, { taxable:0, cgst:0, sgst:0, igst:0, totalGST:0, total:0 });

  // Export handlers...
  const exportPDF = () => {
    const doc = new jsPDF('p','mm','a4');
    doc.setFontSize(16).text(settings.BusinessName||'Business Name',105,16,{align:'center'});
    doc.setFontSize(10);
    settings.Address && doc.text(settings.Address,105,22,{align:'center'});
    settings.GSTIN   && doc.text(`GSTIN: ${settings.GSTIN}`,105,28,{align:'center'});
    settings.Phone   && doc.text(`Contact: ${settings.Phone}`,105,34,{align:'center'});
    doc.setFontSize(13).setTextColor(22,88,189).text('Purchase Report',105,46,{align:'center'});
    doc.setTextColor(0).setFontSize(10)
       .text(`Period: ${dayjs(from).format('DD/MM/YYYY')} - ${dayjs(to).format('DD/MM/YYYY')}`,105,53,{align:'center'});

    autoTable(doc,{
      startY:60,
      head: [['#','Bill No','Date','Supplier','Taxable','CGST','SGST','IGST','Total GST','Total','Status']],
      body: filteredBills.map((b,i) => {
        const s = calculateSums(b);
        return [
          i+1,
          b.BillNo,
          b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : '',
          b.SupplierName,
          s.taxable.toFixed(2),
          s.cgst.toFixed(2),
          s.sgst.toFixed(2),
          s.igst.toFixed(2),
          s.totalGST.toFixed(2),
          s.total.toFixed(2),
          b.Status
        ];
      }),
      theme:'grid',
      headStyles:{fillColor:[129,164,253]},
      foot: filteredBills.length ? [[
        { content:'Totals', colSpan:4, styles:{halign:'right',fontStyle:'bold'} },
        grandTotals.taxable.toFixed(2),
        grandTotals.cgst.toFixed(2),
        grandTotals.sgst.toFixed(2),
        grandTotals.igst.toFixed(2),
        grandTotals.totalGST.toFixed(2),
        grandTotals.total.toFixed(2),
        ''
      ]] : []
    });
    doc.save(`PurchaseReport_${from}_to_${to}.pdf`);
  };

  const exportExcel = () => {
    const header = [
      [settings.BusinessName||'Business Name'],
      [settings.Address||''],
      [`GSTIN: ${settings.GSTIN||''}`],
      ['Purchase Report'],
      [`Period: ${dayjs(from).format('DD/MM/YYYY')} - ${dayjs(to).format('DD/MM/YYYY')}`],
      [],
      ['#','Bill No','Date','Supplier','Taxable','CGST','SGST','IGST','Total GST','Total','Status']
    ];
    const rows = filteredBills.map((b,i)=>{
      const s = calculateSums(b);
      return [
        i+1,
        b.BillNo,
        b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : '',
        b.SupplierName,
        s.taxable.toFixed(2),
        s.cgst.toFixed(2),
        s.sgst.toFixed(2),
        s.igst.toFixed(2),
        s.totalGST.toFixed(2),
        s.total.toFixed(2),
        b.Status
      ];
    });
    if(filteredBills.length) rows.push([
      '','Totals','','',
      grandTotals.taxable.toFixed(2),
      grandTotals.cgst.toFixed(2),
      grandTotals.sgst.toFixed(2),
      grandTotals.igst.toFixed(2),
      grandTotals.totalGST.toFixed(2),
      grandTotals.total.toFixed(2),
      ''
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([...header, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, 'PurchaseReport');
    XLSX.writeFile(wb, `PurchaseReport_${from}_to_${to}.xlsx`);
  };

  const handleDownloadJSON = () => {
    const period = dayjs(from).format('MMYYYY');
    const payload = toGSTR2JSON(filteredBills, settings.GSTIN, period, settings.StateCode||'29');
    const blob = new Blob([JSON.stringify(payload,null,2)],{type:'application/json'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gstr2_${period}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handlePrint = () => window.print();

  const sendEmailReport = async () => {
    setSending(true);
    try {
      await api.post('/send-purchase-report',{ email, from, to, company: settings, bills: filteredBills });
      alert('Email sent!');
      setShowEmailModal(false);
    } catch {
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={printRef} className="p-4">
      {/* Filters */}
      <div className="row g-3 align-items-end mb-4 no-print">
        <div className="col-auto">
          <label className="form-label">From</label>
          <input
            type="date"
            className="form-control"
            value={from}
            onChange={e=>setFrom(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label">To</label>
          <input
            type="date"
            className="form-control"
            value={to}
            onChange={e=>setTo(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button
            className="btn btn-outline-primary"
            onClick={handleThisMonth}
          >
            This Month
          </button>
        </div>
        <div className="col-auto">
          <label className="form-label">Supplier</label>
          <select
            className="form-select"
            value={filterSupplier}
            onChange={e=>setFilterSupplier(e.target.value)}
          >
            <option value="">All Suppliers</option>
            {suppliers.map(s=>(
              <option key={s.SupplierID} value={s.SupplierID}>{s.Name}</option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={filterStatus}
            onChange={e=>setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option>Paid</option>
            <option>Partial</option>
            <option>Pending</option>
          </select>
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={fetchData}>Get Report</button>
        </div>
      </div>

      {/* Actions */}
      {filteredBills.length > 0 && (
        <div className="mb-4 no-print">
          <div className="btn-group me-2">
            <button className="btn btn-success" onClick={exportPDF}>PDF</button>
            <button className="btn btn-info text-white" onClick={exportExcel}>Excel</button>
            <button className="btn btn-outline-secondary" onClick={handleDownloadJSON}>
              GSTR‑2 JSON
            </button>
            <button className="btn btn-outline-dark" onClick={handlePrint}>Print</button>
          </div>
          <button className="btn btn-dark" onClick={()=>setShowEmailModal(true)}>Email</button>
        </div>
      )}

      {/* Summary */}
      <div className="row mb-4">
        <div className="col-md-4 mb-2">
          <div className="card text-center">
            <div className="card-body">
              <div className="text-muted">Taxable Total</div>
              <h5>₹{grandTotals.taxable.toFixed(2)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card text-center">
            <div className="card-body">
              <div className="text-muted">Total GST</div>
              <h5>₹{grandTotals.totalGST.toFixed(2)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card text-center">
            <div className="card-body">
              <div className="text-muted">Grand Total</div>
              <h5>₹{grandTotals.total.toFixed(2)}</h5>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped align-middle">
          <thead className="table-dark">
            <tr>
              <th>#</th><th>Bill No</th><th>Date</th><th>Supplier</th>
              <th>Taxable</th><th>CGST</th><th>SGST</th><th>IGST</th>
              <th>Total GST</th><th>Total</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredBills.map((b,i)=> {
              const s = calculateSums(b);
              return (
                <tr key={b.PurchaseBillID}>
                  <td>{i+1}</td>
                  <td>{b.BillNo}</td>
                  <td>{b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : ''}</td>
                  <td>{b.SupplierName}</td>
                  <td>₹{s.taxable.toFixed(2)}</td>
                  <td>₹{s.cgst.toFixed(2)}</td>
                  <td>₹{s.sgst.toFixed(2)}</td>
                  <td>₹{s.igst.toFixed(2)}</td>
                  <td>₹{s.totalGST.toFixed(2)}</td>
                  <td>₹{s.total.toFixed(2)}</td>
                  <td>
                    <span className={`badge bg-${b.Status==='Paid'?'success':b.Status==='Partial'?'warning':'danger'}`}>
                      {b.Status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredBills.length === 0 && (
              <tr><td colSpan={11} className="text-center">No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal d-block no-print" tabIndex="-1">
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <form onSubmit={e=>{e.preventDefault(); sendEmailReport();}}>
                <div className="modal-header">
                  <h5 className="modal-title">Send Report</h5>
                  <button type="button" className="btn-close" onClick={()=>setShowEmailModal(false)}/>
                </div>
                <div className="modal-body">
                  <label>Recipient Email</label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={()=>setShowEmailModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={sending}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
